import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "./fordon";
import { faqSchema } from "@/components/HubEditorial";
import { AdSlot } from "@/components/AdSlot";
import { RelatedLinks } from "@/components/RelatedLinks";
import { StockCard } from "@/components/StockCard";
import { getStockBoard } from "@/lib/stocks.functions";
import { STOCK_ISSUERS } from "@/lib/stocks";
import { HUSBIL_BRANDS, HUSVAGN_BRANDS } from "@/data/vehicle-brands";
import { PrisPuls } from "@/components/PrisPuls";

const URL = "https://www.akhusbilfritid.se/borsen";
const TITLE = buildTitle("Börsen för husbilsmärken – Kabe, Trigano");
const DESC =
  "Aktiekurser för bolagen bakom husbilsmärkena: Kabe, Trigano, Knaus Tabbert, Thor (Hymer, Dethleffs), Dometic och Thule — kopplat till våra egna annonser.";

const FAQ = [
  {
    q: "Vilka husbilsmärken ägs av börsnoterade bolag?",
    a: "Kabe och Polar tillhör KABE Group på Nasdaq Stockholm. Adria, Chausson och Caravelair ägs av franska Trigano. Knaus, Weinsberg och Tabbert tillhör Knaus Tabbert AG i Frankfurt. Hymer, Dethleffs och Bürstner ingår i Erwin Hymer Group som ägs av amerikanska Thor Industries. Rapido och Cabby är familjeägda och finns inte på någon börs.",
  },
  {
    q: "Varför ska en husbilsköpare bry sig om aktiekursen?",
    a: "Kursen i sig påverkar inte vad du betalar, men bolagens rapporter gör det. När orderböckerna krymper kortas leveranstiderna och rabatterna växer på nya fordon, vilket också pressar begagnatpriserna. När orderböckerna är fulla får du vänta längre och begagnat blir dyrare. Aktien är den snabbaste offentliga signalen på vilket läge vi är i.",
  },
  {
    q: "Hur ofta uppdateras kurserna?",
    a: "Kurserna hämtas löpande från börsdata och cachas i tio minuter. De är fördröjda och avsedda som marknadsöversikt, inte som underlag för aktiehandel.",
  },
  {
    q: "Hur hänger det ihop med era annonser?",
    a: "Varje bolag på sidan länkar till de fordon vi har i lager från deras märken, och på varje märkessida visar vi bolagets kurs. Vill du i stället se vad fordonen kostar över tid finns AK Prisindex, som mäter prisnivån på annonserna vi följer månad för månad.",
  },
  {
    q: "Vad gör Dometic och Thule på listan?",
    a: "De tillverkar inte fordon utan det som sitter i dem: kyl, luftkonditionering, markiser, cykelhållare och förtält. Deras eftermarknadsförsäljning visar hur mycket folk använder sina fordon, vilket ofta vänder före själva fordonsförsäljningen.",
  },
];

export const Route = createFileRoute("/borsen")({
  codeSplitGroupings: [["loader", "component"]],
  loader: async () => ({ board: await getStockBoard() }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "kabe aktie, trigano aktie, knaus tabbert aktie, thor industries hymer, dometic aktie, thule aktie, husbilsbranschen börsen",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Börsen för husbilsmärken — bolagen bakom fordonen" },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQ)) }],
  }),
  component: Page,
});

function brandLinks(slugs: string[]) {
  return slugs.flatMap((slug) => {
    const hb = HUSBIL_BRANDS.find((b) => b.slug === slug);
    const hv = HUSVAGN_BRANDS.find((b) => b.slug === slug);
    return [
      ...(hb ? [{ slug, name: `${hb.name} husbil`, category: "husbil" as const }] : []),
      ...(hv ? [{ slug, name: `${hv.name} husvagn`, category: "husvagn" as const }] : []),
    ];
  });
}

function Page() {
  const { board } = Route.useLoaderData();

  return (
    <>
      <PageHeader
        label="Data · Bolagen bakom märkena"
        title={
          <>
            Börsen för <span className="font-italic-display text-accent">husbilsmärkena</span>
          </>
        }
        sub="Kabe, Trigano, Knaus Tabbert, Thor, Dometic och Thule — kurs, utveckling och vad respektive bolag faktiskt tillverkar. Varje bolag är kopplat till de fordon vi har i lager från deras märken."
      />

      <PrisPuls />



      <section className="container-edge grid gap-6 pt-8 md:pt-12 lg:grid-cols-2">
        {STOCK_ISSUERS.map((issuer) => {
          const quote = board.quotes.find((q) => q.id === issuer.id);
          if (!quote) return null;
          return (
            <StockCard
              key={issuer.id}
              issuer={issuer}
              quote={quote}
              brandLinks={brandLinks(issuer.brandSlugs)}
            />
          );
        })}
      </section>

      <section className="container-edge pt-10">
        <AdSlot variant="in-article" />
      </section>

      <section className="container-edge pt-12 md:pt-16">
        <div className="prose-editorial max-w-3xl space-y-5 text-[0.975rem] leading-relaxed text-muted-foreground">
          <h2 className="font-display text-2xl text-foreground">
            Så läser du branschen genom bolagens siffror
          </h2>
          <p>
            Fritidsfordon är en cyklisk bransch, och cykeln syns tidigare i bolagens rapporter än i
            annonspriserna. Ordningen är nästan alltid densamma: först vänder tillverkarnas orderbok,
            sedan justeras produktionstakten, därefter kommer kampanjpriserna på nya fordon och först
            till sist rör sig begagnatnivån. Det betyder att en köpare som läser Kabes eller Knaus
            Tabberts kvartalsrapport har ett halvårs försprång på den som bara tittar på
            annonspriser.
          </p>
          <p>
            Titta särskilt på tre saker: orderstocken jämfört med samma kvartal förra året,
            lagernivån hos återförsäljarna och bruttomarginalen. Växande återförsäljarlager plus
            fallande marginal är signalen på att rabatterna är på väg. Full orderbok och stigande
            marginal betyder motsatsen — då är det läge att köpa begagnat innan nyprislistan drar upp
            hela nivån.
          </p>
          <h2 className="font-display text-2xl text-foreground">Vem äger egentligen vad?</h2>
          <p>
            Branschen är betydligt mer koncentrerad än märkesskyltarna antyder. Thor Industries och
            Trigano står tillsammans för en stor del av allt som säljs i Europa, vilket är
            förklaringen till att helt olika märken plötsligt delar chassi, kylskåp och
            inredningsdetaljer. För dig som köpare är det bra nyheter: reservdelar och
            komponentkunskap följer koncernen, inte skylten på fronten. Det är också därför vi kan
            serva och beställa delar till fler märken än vi säljer.
          </p>
          <p>
            Alla märken är dock inte börsnoterade. Rapido är familjeägt franskt, Cabby är svenskt och
            privat, och flera mindre tillverkare ligger i onoterade koncerner. För dem finns ingen
            kurs att följa — där får man i stället läsa marknaden genom{" "}
            <Link to="/prisindex" className="font-medium text-accent underline decoration-accent/40">
              AK Prisindex
            </Link>{" "}
            och{" "}
            <Link
              to="/prisstatistik-husbil"
              className="font-medium text-accent underline decoration-accent/40"
            >
              prisstatistiken
            </Link>
            .
          </p>
          <h2 className="font-display text-2xl text-foreground">Från bolagsdata till ditt köp</h2>
          <p>
            Ska du sälja är bolagens läge en del av tidpunkten:{" "}
            <Link
              to="/salja-husbil"
              className="font-medium text-accent underline decoration-accent/40"
            >
              säljguiden
            </Link>{" "}
            går igenom vad väntan kostar i kronor. Ska du köpa räknar{" "}
            <Link
              to="/vardera-husbil"
              className="font-medium text-accent underline decoration-accent/40"
            >
              värderingsverktyget
            </Link>{" "}
            fram en nivå för det specifika fordonet, och{" "}
            <Link to="/fordon" className="font-medium text-accent underline decoration-accent/40">
              lagerlistan
            </Link>{" "}
            visar vad som finns just nu.
          </p>
          <p className="text-sm">
            Kurserna är fördröjda och hämtas från publika börsdatakällor. Sidan är
            marknadsöversikt för fritidsfordonsköpare — inte investeringsrådgivning, och inget
            underlag för aktiebeslut.
          </p>
          <h2 className="font-display text-2xl text-foreground">Vanliga frågor</h2>
          <dl className="space-y-4">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-medium text-foreground">{f.q}</dt>
                <dd className="mt-1">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="container-edge pt-12">
        <RelatedLinks
          keyId="borsen"
          text="börsen aktie kabe trigano knaus thor dometic thule husbilsbranschen marknad prisindex"
          currentHref="/borsen"
        />
        <div className="pt-10">
          <AdSlot variant="multiplex" label="Relaterat innehåll" />
        </div>
      </section>
    </>
  );
}
